import styled, { css } from 'styled-components';
import { Button } from 'antd';
import { LuCheck, LuX } from 'react-icons/lu';

/**
 * The assistant's answer in the count panel (design: corrections_design.html, section 4a): how many it keeps,
 * rejects and found missing, then each proposal numbered as on the video, with ✓ (apply it) and ✗ (keep mine), and
 * Keep mine / Apply for all of them. Nothing counts until it is applied.
 */

const tk = ({ theme: t }) => t.token;

const Box = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).marginXS}px;
`;

const Head = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: baseline;
	gap: ${p => tk(p).marginXS}px;
`;

const Label = styled.span`
	font-size: ${p => tk(p).fontSizeXXS}px;
	font-weight: ${p => tk(p).fontWeightStrong};
	letter-spacing: 0.09em;
	text-transform: uppercase;
	color: ${p => tk(p).colorTextTertiary};
`;

const Small = styled.span`
	font-size: ${p => tk(p).fontSizeXS}px;
	color: ${p => tk(p).colorTextTertiary};
`;

const Pills = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${p => tk(p).marginXXS}px;
`;

const KIND = {
	keep: p => css`
		background: ${tk(p).colorBrandDark};
		border-color: ${tk(p).colorBrandDark};
		color: ${tk(p).colorTextLightSolid};
	`,
	reject: p => css`
		background: ${tk(p).colorErrorBg};
		border-color: ${tk(p).colorErrorBorderHover};
		color: ${tk(p).colorErrorHover};
	`,
	missing: p => css`
		background: ${tk(p).colorFillQuaternary};
		border-color: ${tk(p).colorBrandLight};
		color: ${tk(p).colorBrandLight};
	`,
};

const Pill = styled.span`
	padding: ${p => `${tk(p).lineWidthBold}px ${tk(p).paddingXS}px`};
	border-radius: ${p => tk(p).borderRadiusLG * 4}px;
	border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType}`};
	font-size: ${p => tk(p).fontSizeXS}px;
	font-weight: ${p => tk(p).fontWeightStrong};
	${p => KIND[p.$kind](p)}
`;

const List = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${p => tk(p).lineWidthBold}px;
	max-height: ${p => tk(p).controlHeight * 7}px;
	overflow: auto;
`;

const Row = styled.div`
	display: grid;
	grid-template-columns: auto 1fr auto;
	align-items: center;
	gap: ${p => tk(p).marginXS}px;
	padding: ${p => `${tk(p).lineWidthBold}px ${tk(p).paddingXXS}px`};
	font-size: ${p => tk(p).fontSizeXS}px;
	color: ${p => tk(p).colorTextSecondary};
	em {
		font-style: normal;
		color: ${p => tk(p).colorTextTertiary};
	}
`;

const Num = styled.i`
	display: grid;
	place-items: center;
	width: ${p => tk(p).controlHeightXS + tk(p).lineWidthBold * 2}px;
	height: ${p => tk(p).controlHeightXS + tk(p).lineWidthBold * 2}px;
	border-radius: 50%;
	font-style: normal;
	font-size: ${p => tk(p).fontSizeXS}px;
	font-weight: ${p => tk(p).fontWeightStrong};
	${p => KIND[p.$kind](p)}
`;

const YesNo = styled.span`
	display: inline-flex;
	gap: ${p => tk(p).lineWidthBold}px;
	button {
		display: grid;
		place-items: center;
		padding: ${p => `${tk(p).lineWidthBold}px ${tk(p).paddingXXS}px`};
		border: ${p => `${tk(p).lineWidth}px ${tk(p).lineType} ${tk(p).colorBorder}`};
		border-radius: ${p => tk(p).borderRadiusSM}px;
		background: ${p => tk(p).colorFillQuaternary};
		color: ${p => tk(p).colorTextSecondary};
		cursor: pointer;
		&:hover {
			border-color: ${p => tk(p).colorTextTertiary};
		}
	}
`;

// side by side when they fit; a long "Apply · remove 1, add 2" goes to its own line instead of widening the panel
const Buttons = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: ${p => tk(p).marginXS}px;
	> * {
		flex: 1 1 auto;
	}
`;

// numbered proposals in the order the video shows them (VideoProjectView numbers them the same way)
export const numberProposals = proposals => proposals.map((pr, i) => ({ ...pr, n: i + 1 }));

const AssistantAnswer = ({ proposals, subject, at = '', kept = null, onAccept, onDismiss }) => {
	if (!proposals?.length) return null;
	const numbered = numberProposals(proposals);
	const rejects = numbered.filter(pr => pr.type !== 'add');
	const missing = numbered.filter(pr => pr.type === 'add');
	const applyLabel = [
		rejects.length && `remove ${rejects.length}`,
		missing.length && `add ${missing.length}`,
	]
		.filter(Boolean)
		.join(', ');
	return (
		<Box>
			<Head>
				<Label>The assistant&apos;s answer</Label>
				{at && <Small>{at}</Small>}
			</Head>
			<Pills>
				{kept != null && <Pill $kind="keep">keep {kept}</Pill>}
				<Pill $kind="reject">reject {rejects.length}</Pill>
				<Pill $kind="missing">missing {missing.length}</Pill>
			</Pills>
			<List>
				{numbered.map(pr => (
					<Row key={pr.id}>
						<Num $kind={pr.type === 'add' ? 'missing' : 'reject'}>{pr.n}</Num>
						<span>
							{pr.type === 'add' ? 'missing' : 'reject'} ·{' '}
							<em>{pr.type === 'add' ? subject : pr.reason || `not a ${subject}`}</em>
						</span>
						<YesNo>
							<button
								type="button"
								title="Apply"
								aria-label="Apply"
								onClick={() => onAccept(pr.id)}>
								<LuCheck />
							</button>
							<button
								type="button"
								title="Keep mine"
								aria-label="Keep mine"
								onClick={() => onDismiss(pr.id)}>
								<LuX />
							</button>
						</YesNo>
					</Row>
				))}
			</List>
			<Buttons>
				<Button onClick={() => numbered.forEach(pr => onDismiss(pr.id))}>Keep mine</Button>
				<Button type="primary" onClick={() => numbered.forEach(pr => onAccept(pr.id))}>
					Apply · {applyLabel}
				</Button>
			</Buttons>
		</Box>
	);
};

export default AssistantAnswer;
